import React, { Children, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SwiperCore, { Mousewheel, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMediaQuery } from 'react-responsive';

import 'swiper/swiper-bundle.min.css';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import useIsBrowser from 'hooks/useIsBrowser';
import isNewConsumerApp from 'commons/isNewConsumerApp';

SwiperCore.use([Mousewheel, Scrollbar]);

const CARDS_PER_VIEW_XS = 1.35;
const CARDS_PER_VIEW_SM = 2.15;
const CARDS_PER_VIEW_MD = 3.1;
const CARDS_PER_VIEW_LG = 4;

const CARDS_PER_VIEW_MINI_XS = 1.8;
const CARDS_PER_VIEW_MINI_SM = 2.75;
const CARDS_PER_VIEW_MINI_MD = 3.75;
const CARDS_PER_VIEW_MINI_LG = 5;

const SPACE_BETWEEN_CARDS_SM = 14;
const SPACE_BETWEEN_CARDS_MD = 24;

const CardSlider = ({ children, size = 'standard', title, wide = true }) => {
  const isBrowser = useIsBrowser();
  // In React 18, it is critical to render identical HTML output during the
  // initial render in both browser and server environments.
  const renderDefaultInNewConsumerApp = isNewConsumerApp && !isBrowser;

  const isLargeMobileMin = useMediaQuery({
    query: '(min-width: 640px)',
  });

  const isTabletMin = useMediaQuery({
    query: '(min-width: 768px)',
  });

  const isDesktopMin = useMediaQuery({
    query: '(min-width: 1024px)',
  });

  const slidesPerView = useMemo(() => {
    if (isDesktopMin && !renderDefaultInNewConsumerApp) {
      return size === 'mini' ? CARDS_PER_VIEW_MINI_LG : CARDS_PER_VIEW_LG;
    }

    if (isTabletMin && !renderDefaultInNewConsumerApp) {
      return size === 'mini' ? CARDS_PER_VIEW_MINI_MD : CARDS_PER_VIEW_MD;
    }

    if (isLargeMobileMin && !renderDefaultInNewConsumerApp) {
      return size === 'mini' ? CARDS_PER_VIEW_MINI_SM : CARDS_PER_VIEW_SM;
    }

    return size === 'mini' ? CARDS_PER_VIEW_MINI_XS : CARDS_PER_VIEW_XS;
  }, [
    isDesktopMin,
    isLargeMobileMin,
    isTabletMin,
    renderDefaultInNewConsumerApp,
    size,
  ]);

  const spaceBetween =
    isTabletMin && !renderDefaultInNewConsumerApp
      ? SPACE_BETWEEN_CARDS_MD
      : SPACE_BETWEEN_CARDS_SM;

  const numberOfCards = Children.count(children);

  const isSliderRequired = useMemo(() => {
    if (isDesktopMin && !renderDefaultInNewConsumerApp) {
      return numberOfCards >= 5;
    }

    if (isTabletMin && !renderDefaultInNewConsumerApp) {
      return numberOfCards >= 4;
    }

    if (isLargeMobileMin && !renderDefaultInNewConsumerApp) {
      return numberOfCards >= 3;
    }

    return numberOfCards >= 2;
  }, [
    isDesktopMin,
    isLargeMobileMin,
    isTabletMin,
    numberOfCards,
    renderDefaultInNewConsumerApp,
  ]);

  return (
    <div className="max-w-[100vw] overflow-hidden">
      {!!title && (
        <FixedWidthContainer wide={wide}>
          <h4 className="text-xl font-bold text-gray mt-3 mx-0 mb-[7px]">
            {title}
          </h4>
        </FixedWidthContainer>
      )}

      <FixedWidthContainer
        wide={wide}
        className={classNames({
          // In new consumer app, if not on a small mobile (the default) then
          // hide the slider until the component has mounted to prevent a flash
          // of the incorrect layout.
          'min-[640px]:hidden': renderDefaultInNewConsumerApp,
        })}
      >
        {isSliderRequired ? (
          <Swiper
            freeMode
            mousewheel={{
              forceToAxis: true,
            }}
            scrollbar={{ draggable: true }}
            slidesPerView={slidesPerView}
            spaceBetween={spaceBetween}
            className="overflow-visible pb-4"
          >
            {Children.map(children, item => (
              <SwiperSlide className="pt-[5px]">{item}</SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="flex">
            {Children.map(children, item => (
              <div
                className={classNames(
                  'pt-[5px] flex-1 ml-3.5 first:ml-0 md:ml-6',
                  {
                    'max-w-[74.07407%] sm:max-w-[calc((100%_-_14px)_/2.15)] md:max-w-[calc((100%_-_48px)_/3.1)] lg:max-w-[calc((100%_-_72px)_/4)]':
                      size !== 'mini',
                    'max-w-[55.55556%] sm:max-w-[calc((100%_-_14px)_/2.75)] md:max-w-[calc((100%_-_48px)_/3.75)] lg:max-w-[calc((100%_-_72px)_/5)]':
                      size === 'mini',
                  }
                )}
              >
                {item}
              </div>
            ))}
          </div>
        )}
      </FixedWidthContainer>
    </div>
  );
};

CardSlider.propTypes = {
  children: PropTypes.node,
  size: PropTypes.oneOf(['mini', 'standard']),
  title: PropTypes.string,
  wide: PropTypes.bool,
};

export default CardSlider;
